import { EXPORT_CHART_AS_IMAGE_WIDTH } from '@modules/common/constants';
import { useExportChartAsImage } from '@modules/common/hooks';
import { useGetDashboardAnalysisLinkWithState } from '@modules/dashboard/hooks';
import { useExportQuantifyFeedback } from '@modules/quantify/components/QuantifyAnalysisToolbarV2/hooks/useExportQuantifyFeedback';
import { useExportQuantifyReportDataAsCSV } from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { useThemeStore } from '@modules/theme/store/useThemeStore';
import {
  CalendarViewMonthRounded,
  ChatRounded,
  ChevronRightRounded,
  SimCardDownloadRounded,
} from '@mui/icons-material';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  Stack,
} from '@mui/material';
import { AnalysisViewFragment } from 'generated/graphql';
import { useRef, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { useGetUser } from 'v2/core/services/user';

const trackQuantifyReportEvents = createAnalyticsTracker('quantifyReport');
const trackQuantifyExportImageEvents = createAnalyticsTracker(
  'quantifyExportChartAsImage',
);

interface DashboardQuantifyExportFeedbackActionProps {
  analysis: AnalysisViewFragment;
  analysisLink: string;
}

export const DashboardQuantifyExportFeedbackAction = ({
  analysis,
  analysisLink,
}: DashboardQuantifyExportFeedbackActionProps) => {
  const [exportMenuPopperAnchorEl, setExportMenuPopperAnchorEl] =
    useState<null | HTMLElement>(null);
  const exportMenuPopperAnchorRef = useRef<null | HTMLElement>(null);

  const { mode } = useThemeStore(({ mode }) => ({ mode }));
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const { analysisLink: analysisLinkWithGlobalFilters } =
    useGetDashboardAnalysisLinkWithState(analysis, mode);

  const { exportChartAsImage: exportChartAsImageAPI } = useExportChartAsImage({
    chartTitle: analysis.title,
    chartURLSlug: `report${analysisLinkWithGlobalFilters}`,
    imageSettings: {
      width: EXPORT_CHART_AS_IMAGE_WIDTH,
    },
  });

  const visualisationType = useQuantifyStore(
    ({ query }) => query.visualisationType,
  );

  const openExportFeedbackRecordsModal = useExportQuantifyFeedback({
    view: 'dashboard',
  });

  const [exportAsCSV] = useExportQuantifyReportDataAsCSV();
  const handleExportTableDataAsCSV = () => {
    exportAsCSV();
    trackQuantifyReportEvents({
      eventName: 'Quantify Report - Download CSV',
      name: analysis.title,
      link: `${window.location.origin}/${tenantSlug}${analysisLink}`,
      scope: mapFromEdgeScope(analysis.scope),
      from: window.location.href,
    });
  };

  const exportChartAsImage = () => {
    exportChartAsImageAPI();
    trackQuantifyExportImageEvents({
      eventName: 'Clicked Export As Image',
      chartType: visualisationType,
      isSavedAnalysis: !!analysis.id,
      from: window.location.href,
    });
  };

  const handleExportMenuEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (!exportMenuPopperAnchorEl) {
      setExportMenuPopperAnchorEl(event.currentTarget);
      exportMenuPopperAnchorRef.current = event.currentTarget;
    }
  };

  return (
    <>
      <MenuItem
        onMouseEnter={handleExportMenuEnter}
        onMouseLeave={() => setExportMenuPopperAnchorEl(null)}
      >
        <ListItemIcon>
          <GetAppIcon fontSize="small" />
        </ListItemIcon>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <ListItemText>Export</ListItemText>
          <ListItemIcon>
            <ChevronRightRounded fontSize="small" sx={{ marginLeft: 'auto' }} />
          </ListItemIcon>
        </Stack>
      </MenuItem>

      <Popper
        anchorEl={exportMenuPopperAnchorEl}
        open={Boolean(exportMenuPopperAnchorEl)}
        id="Export Options"
        placement="right-start"
        sx={{
          zIndex: (theme) => theme.zIndex.modal,
        }}
        modifiers={[
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['left-start'],
            },
          },
        ]}
        onMouseEnter={() =>
          setExportMenuPopperAnchorEl(exportMenuPopperAnchorRef.current)
        }
        onMouseLeave={() => {
          setExportMenuPopperAnchorEl(null);
          exportMenuPopperAnchorRef.current = null;
        }}
      >
        <Paper elevation={8} sx={{ width: 'auto', py: 1 }}>
          <MenuItem onClick={openExportFeedbackRecordsModal}>
            <ListItemIcon>
              <ChatRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Feedback Records</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportTableDataAsCSV}>
            <ListItemIcon>
              <CalendarViewMonthRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Table (CSV)</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportChartAsImage}>
            <ListItemIcon>
              <SimCardDownloadRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Chart (Image)</ListItemText>
          </MenuItem>
        </Paper>
      </Popper>
    </>
  );
};
